import { useEffect, useState, useContext } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAppStore } from 'Store/App'
import { useProjectStore } from 'Store/Project'
import { useMachineStore } from 'Store/Machine'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { useInventoryStore } from '../../../../Store/Inventory'
import { PileExecutionAttachmentContext } from '../PileExecutionAttachment/Context'
import Swal from 'sweetalert2'
import {
  inferScanTypeFromCode,
} from 'Utils/scanInventoryType'

interface INITIAL_ADDITIONAL_FORM_STATE {
  projectMachineId: number | null
  pileNo: string | null
  gridLine: string | null
  location: string | null
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { setOpen, setCurrentPileHoleId } = useContext(PileExecutionAttachmentContext)
  const { id: projectId } = useParams<{ id: string }>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [searchParams] = useSearchParams()
  const [selected, setSelected] = useState<'user' | 'environment' | undefined>('environment')
  const [startScan, setStartScan] = useState(false)
  const [loadingScan, setLoadingScan] = useState(false)
  const [scannedData, setScannedData] = useState(null)
  const [isUseCamera, setIsUseCamera] = useState(false)
  const [hasCamera, setHasCamera] = useState(false)
  const pileHoleId = searchParams.get('pileHoleId')
  const [additionalFormState, setAdditionalFormState] = useState<INITIAL_ADDITIONAL_FORM_STATE>({
    projectMachineId: null,
    pileNo: null,
    gridLine: null,
    location: null,
  })

  // const scanReceive = useInventoryStore(state => state.selected, shallow)

  const setShowBackButton = useAppStore(state => state.setShowBackButton, shallow)

  const { inventorySelected, getInventory, setSelectedInventory } = useInventoryStore(
    state => ({
      inventorySelected: state.selected,
      getInventory: state.getOne,
      setSelectedInventory: state.setSelected,
    }),
    shallow
  )
  const { machineData, getMachineData } = useMachineStore(
    state => ({
      machineData: state.data,
      getMachineData: state.getAll,
    }),
    shallow
  )

  const {
    projectData,
    getOne,
    pileHole,
    pileDriving,
    projectMachines,
    getMachines,
    getPileHoleInfo,
    removePileDriving,
    closeHole,
    setPileHole,
    openProject,
    closeProject,
  } = useProjectStore(
    state => ({
      projectData: state.selected,
      getOne: state.getOne,
      pileHole: state.pileHole,
      pileDriving: state.pileDriving,
      projectMachines: state.machines,
      getPileHoleInfo: state.getPileHoleInfo,
      removePileDriving: state.removePileDriving,
      closeHole: state.closeHole,
      setPileHole: state.setPileHole,
      openProject: state.open,
      closeProject: state.close,
      getMachines: state.getAllProjectMachine,
    }),
    shallow
  )

  const projectMachineOptions = projectMachines.map(pm => {
    const machine = machineData.find(m => m.id === pm.machineId)
    return {
      label: `${machine?.brand ?? ''} ${machine?.machineModel ?? ''} (${machine?.machineCode}) - ${
        machine?.machineType?.title
      }`,
      value: pm.machineId,
    }
  })

  const showAddButtonAttachmentDrivedPile = (index: number) => {
    const inventory = pileHole?.itemInventory ?? []
    const item = inventory[index]

    if (item) {
      if (inventory.length === 1) {
        return false
      } else {
        if ((index - 1) % 2 === 0) {
          return true
        }
      }
    } else {
      return false
    }
  }

  const fetchProject = () => {
    getMachineData()
    getMachines(projectId ? projectId : '0')
    getOne(projectId ? parseInt(projectId) : 0)
  }

  const fetchPileHoleInfo = () => {
    if (pileHoleId && projectId) {
      getPileHoleInfo(parseInt(projectId), parseInt(pileHoleId))
    }
  }

  const onViewList = () => {
    navigate(`/project/${projectId}/pile-executed-list`)
  }

  const handleScan = async (scanData: any) => {
    setLoadingScan(true)
    const payload = scanData[0].rawValue

    if (payload && payload !== '') {
      setScannedData(payload)
      if (!!payload) {
        const resp = await getInventory(payload)
        if (resp.success) {
          // toast.success('Inventory found')
        } else {
          toast.error('ไม่พบรายการเสาเข็ม')
        }
      }
      setStartScan(false)
      setLoadingScan(false)
    }
  }

  const handleInputCodeChange = (e: any) => {
    setScannedData(e.target.value)
  }

  const handleManualScan = async () => {
    if (!!scannedData) {
      getInventory(scannedData).then(res => {
        if (res.success) {
          if (res.data) {
            if (parseInt(res.data.projectId) !== parseInt(projectId ?? '0')) {
              Swal.fire({
                icon: 'warning',
                title: 'รายการนี้ถูกใช้งานในโครงการอื่น',
                confirmButtonText: 'ปิด',
              })
              setSelectedInventory(null)
              setScannedData(null)
              return
            }
          }
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'ไม่พบรหัสเสาเข็มนี้อยู่ในโครงการนี้',
            confirmButtonText: 'ปิด',
          })
        }
      })
    }
  }

  const handleRemoveScanned = async (inventoryCode: string) => {
    if (pileHole?.id && projectId) {
      await removePileDriving(parseInt(projectId), {
        inventoryCode,
        pileHoleId: pileHole?.id,
        type: inferScanTypeFromCode(inventoryCode),
      })
      await getPileHoleInfo(parseInt(projectId), pileHole.id)
    }
  }

  const handleCancelSelected = () => {
    setSelectedInventory(null)
    setScannedData(null)
  }

  const handlePileDriving = async () => {
    if (!projectId || !scannedData) return
    setIsSubmitting(true)
    try {
      const { success, data } = await pileDriving(parseInt(projectId), {
        inventoryCode: scannedData,
        type: inferScanTypeFromCode(scannedData),
        pileHoleId: pileHole?.id ?? null,
        projectMachineId: additionalFormState.projectMachineId,
        pileNo: additionalFormState.pileNo,
        gridLine: additionalFormState.gridLine,
        location: additionalFormState.location,
      })
      if (!success) {
        setIsSubmitting(false)
        toast.error(data)
        return
      }
      getPileHoleInfo(parseInt(projectId), data.projectPileHoleId)
      setSelectedInventory(null)
      setAdditionalFormState({
        projectMachineId: null,
        pileNo: null,
        gridLine: null,
        location: null,
      })
      setScannedData(null)
      toast.success('Pile Driving Success')
      setIsSubmitting(false)
    } catch (err: any) {
      setIsSubmitting(false)
      toast.error(err.message)
    }
  }
  const handlePileClose = async () => {
    if (!projectId) return
    try {
      await closeHole(parseInt(projectId), {
        pileHoleId: pileHole?.id ?? null,
      })
      setSelectedInventory(null)
      setScannedData(null)
      setPileHole(null)
      toast.success('Pile Close Success')
    } catch (err: any) {
      toast.error(err.message)
    }
  }

  const handleExecutedPileAttachment = (index: number) => {
    const item = pileHole?.itemInventory[index]
    const previousItem = pileHole?.itemInventory[index - 1]

    if (!item || !previousItem) return

    setOpen(true)
    setCurrentPileHoleId({
      pile1HoleId: item?.id ?? '',
      pile2HoleId: previousItem?.id ?? '',
    })
  }

  const toggleUseCamera = () => {
    const nextValue = !isUseCamera
    if (!hasCamera) {
      toast.error('No camera found')
      return
    }
    setIsUseCamera(nextValue)
  }

  const onOpenproject = () => {
    if (projectId) {
      Swal.fire({
        title: 'ยืนยันการเปิดโครงการ',
        text: 'คุณต้องการเปิดโครงการนี้ใช่หรือไม่?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      }).then(result => {
        if (result.isConfirmed) {
          openProject(parseInt(projectId)).then(({ success, data }) => {
            if (!success) {
              toast.error(data)
            } else {
              toast.success('เปิดโครงการสำเร็จ')
              getOne(parseInt(projectId))
            }
          })
        }
      })
    }
  }
  const onCloseproject = () => {
    if (projectId) {
      Swal.fire({
        title: 'ยืนยันการปิดโครงการ',
        text: 'คุณต้องการปิดโครงการนี้ใช่หรือไม่?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
      }).then(result => {
        if (result.isConfirmed) {
          closeProject(parseInt(projectId)).then(({ success, data }) => {
            if (!success) {
              toast.error(data)
            } else {
              toast.success('ปิดโครงการสำเร็จ')
              getOne(parseInt(projectId))
            }
          })
        }
      })
    }
  }

  const onChangeAdditionalFormState = (field: keyof INITIAL_ADDITIONAL_FORM_STATE, value: any) => {
    setAdditionalFormState(prevState => ({
      ...prevState,
      [field]: value,
    }))
  }

  useEffect(() => {
    fetchProject()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  useEffect(() => {
    fetchPileHoleInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, pileHoleId])

  useEffect(() => {
    async function checkCameraAvailability () {
      try {
        // Check if mediaDevices is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          alert('MediaDevices API is not supported in this browser')
          console.warn('MediaDevices API is not supported in this browser')
          setHasCamera(false)
          return
        }

        // Check if we have permission to access devices
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach(track => track.stop()) // Stop the stream immediately

        const devices = await navigator.mediaDevices.enumerateDevices()
        setHasCamera(devices.some(device => device.kind === 'videoinput'))
      } catch (error) {
        console.error('Error checking camera availability:', error)
        setHasCamera(false)
      }
    }

    checkCameraAvailability()
  }, [])

  useEffect(() => {
    setShowBackButton(true)

    return () => {
      setShowBackButton(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isSubmitting,
    projectMachineOptions,
    projectState: projectData?.projectState,
    inventorySelected,
    selected,
    pileHole,
    projectId,
    setSelected,
    startScan,
    setStartScan,
    loadingScan,
    setLoadingScan,
    hasCamera,
    scannedData,
    setScannedData,
    showAddButtonAttachmentDrivedPile,
    handlePileDriving,
    handleScan,
    handleManualScan,
    handleInputCodeChange,
    handleCancelSelected,
    handleRemoveScanned,
    handlePileClose,
    handleExecutedPileAttachment,
    onViewList,
    isUseCamera,
    toggleUseCamera,
    onOpenproject,
    onCloseproject,
    additionalFormState,
    onChangeAdditionalFormState,
  }
}

export default ViewModel
